'use client';
import cn from 'classnames';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import './SinglePageApplication.style.scss';

import { getUtmSource } from '@/util/utm_client';
import { isBrandAmbassadorCode } from '@/util/form_validation_errors';
import { useModal } from '@/components/hook/modal';
import BrandAmbassadorRedirectModal from '@/components/modal/BrandAmbassadorRedirectModal/BrandAmbassadorRedirectModal';
import {
  useAmbassadorRedirectFlag,
  useExperiment
} from '@/components/hook/flags';
import { useOnMount } from '@/components/hook/onMount';
import { useSignup } from '@/components/hook/signup';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { useApplicationSteps } from '@/components/hook/applicationSteps';

import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import { createPreviewSubscriptionCart } from '@/util/user_client';
import analytics from '@/util/analytics';
import { initializeDatadogRUM } from '@/util/datadog';
import { RawEPCCCartResponse } from '@/types/Cart';
import { NextSearchParams } from '@/types/Next';
import { SignupState } from '@/types/Signup';

import { APPLICATION_FLOW } from '@/const/application_flow';
import { FLAG_KEYS } from '@/const/flags';
import { useApplicationSubmit } from '@/components/hook/applicationSubmit';

import type { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';
import MembershipApplicationOverlay from '@/components/common/MembershipApplicationOverlay/MembershipApplicationOverlay';
import {
  ApplicationFormValues,
  validateApplication
} from './applicationValidation';
import {
  applyBillingMirror,
  billingFromSubmission,
  postalCodeMismatchNotice,
  walletBillingMissing,
  walletBillingOverride
} from './addressSync';
import {
  readPersistedSignupState,
  useSignupPersistence
} from './useSignupPersistence';
import { readSetupIntent, SetupIntentStatus } from './checkoutSession';
import { linkReferralCode } from './linkReferralCode';
import ApplicationBody from './ApplicationBody';
import { PAYMENT_APPEARANCE } from './paymentAppearance';
import {
  ConfirmPayment,
  PaymentChoice,
  PaymentStatus,
  paymentChoiceLabel
} from './ApplicationPaymentSection';

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ''
);

/**
 * One Elements instance for the whole page. Both payment surfaces share it: the
 * card fields inside the Billing Method section, and the wallet buttons down by
 * the consents. `mode: 'setup'` is Stripe's deferred flow — nothing is created
 * in Stripe until the applicant submits.
 *
 * `paymentMethodConfiguration` is a Stripe configuration holding exactly what
 * signup accepts: card, Apple Pay, Google Pay. It exists because the methods we
 * do not want cannot all be turned off from the client — Link's pay-by-bank
 * (`link_card_brand`, the "Bank / $5 back" tab) rides along with Link the way
 * the wallets ride along with card, so no `paymentMethodTypes` list removes it.
 *
 * Whichever branch runs here, the SetupIntent route has to be created the same
 * way or Stripe refuses the confirm — so both read the one server-side variable.
 */
function elementsOptionsFor(paymentMethodConfiguration?: string) {
  return {
    mode: 'setup' as const,
    currency: 'usd',
    // Nothing is charged at signup; this only tells Stripe the method is being
    // saved to charge later, once the application is approved.
    setupFutureUsage: 'off_session' as const,
    ...(paymentMethodConfiguration
      ? { paymentMethodConfiguration }
      : { paymentMethodTypes: ['card'] }),
    appearance: PAYMENT_APPEARANCE
  };
}

type SinglePageApplicationProps = {
  className?: string;
  subscriptionData: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  searchParams?: NextSearchParams;
  /**
   * Stripe Payment Method Configuration id, read from the environment on the
   * server. Passed rather than read from `NEXT_PUBLIC_…` here so an environment
   * can change which methods signup offers without a rebuild.
   */
  paymentMethodConfiguration?: string;
};

function SinglePageApplication(props: SinglePageApplicationProps) {
  const {
    className = '',
    subscriptionData,
    insuranceData,
    searchParams,
    paymentMethodConfiguration
  } = props;

  const router = useRouter();
  const { user } = useContextBroker();
  const { value: state, actions: signupActions } = useSignup();
  const steps = useApplicationSteps();
  const ambassadorRedirectModalEnabled = useAmbassadorRedirectFlag();
  // Fires exposure here too — this IS the one_page arm's landing surface; useExperiment's dedupe prevents double-counting with SignUpApp's call.
  useExperiment(FLAG_KEYS.APPLY_SINGLE_PAGE);

  const [previewCart, setPreviewCart] = useState<RawEPCCCartResponse>();
  const [previewCartUpdated, setPreviewCartUpdated] = useState(false);
  const [stripeCouponId, setStripeCouponId] = useState<string | undefined>();
  const [requiresAuthorizationCharge, setRequiresAuthorizationCharge] =
    useState<boolean | undefined>();

  useEffect(() => {
    if (user) {
      router.replace('/account');
    }
  }, [user, router]);

  const referralCopy = useMemo<ReferralCopyProps | undefined>(() => {
    // Ambassador codes never apply here — they're only valid through the
    // dedicated ambassador application — so don't show a success/pending
    // message that promises a discount this flow will not honor.
    if (
      !state.referralCode?.trim() ||
      !previewCartUpdated ||
      (ambassadorRedirectModalEnabled &&
        isBrandAmbassadorCode(state.referralCode))
    ) {
      return undefined;
    }

    return previewCart?.included?.promotions
      ? {
          copy: `Success! ${state.referralCode} applied!`,
          successfullyApplied: true
        }
      : { copy: 'Applied after acceptance', successfullyApplied: false };
  }, [
    previewCart?.included?.promotions,
    previewCartUpdated,
    state.referralCode,
    ambassadorRedirectModalEnabled
  ]);

  /**
   * The one and only preview cart for the page. The old flow ran this twice —
   * once here and once inside `SignUpSummaryModal` — purely because modals
   * cannot inherit updated props from their parent. Inline panel, one effect.
   *
   * This is deliberately **local state**, not part of `SignupContext`: the
   * preview cart is server state and belongs to the panel, not the signup
   * reducer (see the SignupState guard test). The `AbortController` cancels the
   * in-flight request when a selection changes so fast tier-clicking cannot
   * race responses and flash the wrong Monthly.
   */
  useEffect(() => {
    const controller = new AbortController();

    const createPreviewCart = async () => {
      const basePlanId = subscriptionData.find(
        (plan) => plan.slug === state.plan?.slug
      )?.productId;
      const addonPlanId = subscriptionData.find(
        (plan) => plan.slug === state.addon_subscription
      )?.productId;
      const insuranceProductId = insuranceData.find(
        (plan) => plan.slug === state.insurance_level
      )?.id;

      setPreviewCartUpdated(false);

      if (!basePlanId || !insuranceProductId) {
        return;
      }

      const subscriptionProductIds = [basePlanId];
      if (addonPlanId) {
        subscriptionProductIds.push(addonPlanId);
      }

      // Ambassador codes never apply in this flow — don't preview their discount.
      const promotionCodes =
        state.referralCode &&
        !(
          ambassadorRedirectModalEnabled &&
          isBrandAmbassadorCode(state.referralCode)
        )
          ? [state.referralCode]
          : [];

      try {
        const cartResponse = await createPreviewSubscriptionCart(
          {
            insuranceProductId,
            promotionCodes,
            subscriptionProductIds
          },
          controller.signal
        );

        if (cartResponse) {
          setPreviewCart(cartResponse.cart);
          setStripeCouponId(cartResponse.stripeCouponId);
          setRequiresAuthorizationCharge(
            cartResponse.requiresAuthorizationCharge
          );
          setPreviewCartUpdated(true);
        }
      } catch (error) {
        // A superseded request was aborted — the newer one owns the result.
        if ((error as Error)?.name !== 'AbortError') {
          throw error;
        }
      }
    };

    createPreviewCart();

    return () => controller.abort();
  }, [
    state.plan,
    state.subscription_level,
    state.insurance_level,
    state.referralCode,
    state.addon_subscription,
    subscriptionData,
    insuranceData,
    ambassadorRedirectModalEnabled
  ]);

  /**
   * Persist a whitelisted subset of the application to sessionStorage and
   * rehydrate it on reload. This replaces the old `beforeunload` guard (which
   * Chrome half-ignores): instead of nagging the member not to leave, we simply
   * restore their progress if they do.
   */
  const { wasRehydrated } = useSignupPersistence(
    state,
    signupActions.updateFormValues
  );

  useOnMount(() => {
    const planName =
      state.plan?.slug || searchParams?.subscription || 'classique';
    const defaultPlan = subscriptionData.find((s) => s.slug === planName);

    signupActions.updateFormValues({
      plan: defaultPlan as SubscriptionProduct
    });

    const utmSource = searchParams?.utm_source;
    const resolvedUtmSource = utmSource
      ? typeof utmSource === 'string'
        ? utmSource
        : utmSource[0]
      : getUtmSource();

    if (resolvedUtmSource && !state.utm_source) {
      signupActions.updateFormValues({ utm_source: resolvedUtmSource });
    }

    // A landing-page link's `?referral=` wins over a code restored from an
    // earlier visit — it's the one the member just clicked. This has to reach
    // context, not just the form: the preview cart prices the promotion from
    // `state.referralCode`. Runs after `useSignupPersistence`'s rehydrate
    // effect (declared above), so the restore cannot overwrite it.
    const referralFromLink = linkReferralCode(searchParams);
    if (referralFromLink) {
      signupActions.updateFormValues({ referralCode: referralFromLink });
    }

    initializeDatadogRUM();

    // The multi-page flow gated this on `stepIndex === 0`. One page means
    // landing on the application *is* the start.
    console.warn('[segment] Application Start', {
      application_flow: APPLICATION_FLOW.SINGLE_PAGE
    });
    analytics.track('Application Start', {
      application_flow: APPLICATION_FLOW.SINGLE_PAGE
    });
  });

  const initialValues = useMemo<ApplicationFormValues>(() => {
    /**
     * Seed from storage as well as context. `useSignupPersistence` rehydrates
     * into `SignupContext` from an effect, which lands *after* this memo has
     * already run — so a form seeded from context alone renders empty inputs
     * for a session that was in fact restored. Reading storage here is what
     * makes a reload actually put the member's answers back in the fields.
     */
    const restored = readPersistedSignupState() ?? {};
    const seed = { ...state, ...restored };

    return {
      firstName: seed.firstName,
      lastName: seed.lastName,
      email: seed.email,
      phone: seed.phone,
      // Never propagate a password back into the form; require a retype.
      password: '',
      birthday: seed.birthday,
      referralCode: linkReferralCode(searchParams) ?? seed.referralCode,
      instagram: seed.instagram,
      tiktok: seed.tiktok,
      optinPromotion: seed.optinPromotion,
      optinText: seed.optinText,
      address: seed.address,
      aptUnitSuite: seed.aptUnitSuite,
      city: seed.city,
      state: seed.state,
      postalCode: seed.postalCode,
      // Checked by default: most members bill where they ship, and the ones who
      // do not can say so without anyone else typing an address twice.
      sameAsShippingAddress: seed.sameAsShippingAddress ?? true,
      billingAddress: seed.billingAddress,
      billingAptUnitSuite: seed.billingAptUnitSuite,
      billingCity: seed.billingCity,
      billingState: seed.billingState,
      billingPostalCode: seed.billingPostalCode,
      // Never restored from a rehydrated session — consent has to be given by
      // the person sitting there now, not replayed from storage.
      paymentAgreement: false,
      terms: false
    };
    // Initial values only — deliberately not resynced on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const elementsOptions = useMemo(
    () => elementsOptionsFor(paymentMethodConfiguration),
    [paymentMethodConfiguration]
  );

  if (user) {
    return null;
  }

  return (
    <div className={cn('SinglePageApplication', className)}>
      {/*
        The provider wraps the whole form because two elements need it in
        different places — the card fields in the Billing Method section and the
        wallet buttons beneath the consents.
      */}
      <Elements
        stripe={stripePromise}
        options={elementsOptions}
      >
        <ApplicationForm
          initialValues={initialValues}
          steps={steps}
          user={state}
          subs={subscriptionData}
          insuranceData={insuranceData}
          previewCart={previewCart}
          previewCartUpdated={previewCartUpdated}
          referralCopy={referralCopy}
          stripeCouponId={stripeCouponId}
          requiresAuthorizationCharge={requiresAuthorizationCharge}
          onCommitValues={signupActions.updateFormValues}
          suppressAbandon={wasRehydrated}
        />
      </Elements>
    </div>
  );
}

type ApplicationFormProps = {
  initialValues: ApplicationFormValues;
  steps: ReturnType<typeof useApplicationSteps>;
  user: SignupState;
  subs: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  previewCart?: RawEPCCCartResponse;
  /**
   * False while a tier/add-on/coverage change has been made but the repriced
   * cart hasn't come back yet — lets the price displays show they're
   * updating instead of sitting on the old number until it snaps to the new
   * one.
   */
  previewCartUpdated: boolean;
  referralCopy?: ReferralCopyProps;
  stripeCouponId?: string;
  requiresAuthorizationCharge?: boolean;
  onCommitValues: (values: Partial<SignupState>) => void;
  /** True when the session was rehydrated — suppresses the Braze abandon fire. */
  suppressAbandon: boolean;
};

/**
 * Owns the Formik form and the submission.
 *
 * Submit confirms the payment method first (which is also what brings the
 * Stripe customer into existence — nothing was created while the form was being
 * filled), then provisions the EPCC account through `useApplicationSubmit`, the
 * same pipeline the multi-page review step runs.
 */
function ApplicationForm(props: ApplicationFormProps) {
  const {
    initialValues,
    steps,
    user,
    subs,
    insuranceData,
    previewCart,
    previewCartUpdated,
    referralCopy,
    stripeCouponId,
    requiresAuthorizationCharge,
    onCommitValues,
    suppressAbandon
  } = props;

  const [paymentError, setPaymentError] = useState<string | null>(null);

  /** Shown above the sections after a postal-code refusal; see `reviewBilling`. */
  const [addressNotice, setAddressNotice] = useState<string | null>(null);
  /** After a wallet's address was refused: the form's billing address replaces it. */
  const [correctWalletBilling, setCorrectWalletBilling] = useState(false);
  const formikRef = useRef<FormikProps<ApplicationFormValues>>(null);

  const modalTools = useModal();
  const ambassadorRedirectModalEnabled = useAmbassadorRedirectFlag();

  /** Ambassador codes only work in the ambassador flow; hand the form over. */
  const showAmbassadorRedirectModal = (values: Partial<SignupState>) => {
    modalTools.showModal(
      <BrandAmbassadorRedirectModal
        onClose={modalTools.closeModal}
        getRegularApplySnapshot={() => ({ ...user, ...values, password: '' })}
      />,
      {
        onClose: modalTools.closeModal,
        topClassName: 'BrandAmbassadorRedirectModal-modal'
      }
    );
  };

  // A `?referral=INFLUENCER...` link seeds the code without any commit.
  useEffect(() => {
    if (
      ambassadorRedirectModalEnabled &&
      isBrandAmbassadorCode(initialValues.referralCode)
    ) {
      showAmbassadorRedirectModal(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambassadorRedirectModalEnabled]);

  /**
   * Confirming lives inside `<Elements>`, which is inside the payment section;
   * Submit is at the bottom of the form, outside it. The section hands its
   * confirm function up here so one button can do both.
   */
  const confirmPaymentRef = useRef<ConfirmPayment | null>(null);

  const handleConfirmReady = useCallback((confirm: ConfirmPayment | null) => {
    confirmPaymentRef.current = confirm;
  }, []);

  /** What the card element reports about itself — completeness and method. */
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>({
    complete: false,
    type: null
  });

  /**
   * How the applicant said they will pay. Card is the default because it is the
   * only method guaranteed to be available in every browser.
   */
  const [paymentChoice, setPaymentChoice] = useState<PaymentChoice>('card');

  /** The wallet actually used, once its sheet has confirmed. */
  const [walletType, setWalletType] = useState<string | null>(null);

  /**
   * The card's billing ZIP failed Stripe's check. The sheet or card form is
   * gone, so put the member where they can fix it: Billing open, "same as
   * shipping" unticked and the address that was checked filled in (the wallet's,
   * or the shipping address copied over), back on the card path. The wallet
   * would only resend the same address. Shipping stays collapsed, but its recap
   * still shows the address; only one section opens at a time.
   */
  const reviewBilling = (submitted: SignupState) => {
    const wasWallet = paymentChoice !== 'card';

    formikRef.current?.setValues(
      (current) => ({ ...current, ...billingFromSubmission(submitted) }),
      false
    );
    // A wallet stays selected: the member corrects the address here and the
    // retry sends it in place of the wallet's.
    setCorrectWalletBilling(wasWallet);
    setPaymentError(null);
    setAddressNotice(
      postalCodeMismatchNotice(
        wasWallet
          ? paymentChoiceLabel(paymentChoice, walletType).replace(
              'Pay with ',
              ''
            )
          : undefined
      )
    );
    steps.requestOpen('billing', true);
  };

  const {
    submitApplication,
    errorMessage,
    setErrorMessage,
    isSubmitting,
    setIsSubmitting
  } = useApplicationSubmit({
    onPostalCodeMismatch: reviewBilling,
    applicationFlow: APPLICATION_FLOW.SINGLE_PAGE,
    paymentMethod: paymentChoice,
    user,
    subscriptionData: subs,
    insuranceData,
    previewCart,
    stripeCouponId,
    requiresAuthorizationCharge
  });

  /**
   * The half of submission that runs after a payment method exists: read the
   * SetupIntent back, then provision the account. Shared by both payment paths —
   * the card path confirms from the page's Submit, the wallet path from its own
   * button — because the only thing that differs is how the method was
   * collected.
   *
   * Flip `isSubmitting` here (not only inside `submitApplication`) so the
   * wallet path — which never goes through Formik submit — still shows the
   * overlay during SetupIntent read. The card path is already covered by
   * Formik's own `isSubmitting` for confirm + everything after.
   */
  const finishApplication = async (
    setupIntentId: string,
    values: ApplicationFormValues
  ) => {
    setIsSubmitting(true);
    setAddressNotice(null);

    // The page collects one address; billing is derived from it so the shared
    // address assembly sees both, populated identically.
    const committed = applyBillingMirror(values);

    let setup: SetupIntentStatus;

    try {
      setup = await readSetupIntent(setupIntentId);
    } catch (error) {
      setPaymentError(
        'We could not confirm your payment method. Please try again.'
      );
      setIsSubmitting(false);
      return;
    }

    if (setup.status !== 'complete' || !setup.customerId) {
      setPaymentError(
        'Your payment method was not saved. Please try a different one.'
      );
      setIsSubmitting(false);
      return;
    }

    if (!correctWalletBilling && walletBillingMissing(setup.paymentMethod)) {
      setPaymentError(
        'Your wallet did not share a billing address. Please try again or pay with a card.'
      );
      setIsSubmitting(false);
      return;
    }

    /**
     * Card details are read back from Stripe rather than off the form — with a
     * wallet there is nothing on the page to read, since Apple Pay and Google
     * Pay never expose the card.
     */
    const submittedUser: SignupState = {
      ...user,
      ...committed,
      ...(!correctWalletBilling &&
        walletBillingOverride(setup.paymentMethod, committed)),
      paymentSetupIntentId: setupIntentId,
      creditCardLast4: setup.paymentMethod?.last4 ?? '',
      expirationDate: setup.paymentMethod?.expirationDate ?? '',
      stripePaymentMethodBrand: setup.paymentMethod?.brand ?? ''
    };

    onCommitValues(committed);

    await submitApplication(submittedUser, {
      kind: 'checkout-session',
      customerId: setup.customerId,
      setupIntentId
    });
  };

  /**
   * Confirm first, provision second. This is also where the Stripe customer
   * comes into existence — nothing was created while they were filling the form
   * — so a declined card leaves neither a customer nor an EPCC account behind.
   *
   * Formik sets `isSubmitting` for the life of this promise, which is what
   * keeps the overlay up through Stripe confirm on slow networks.
   */
  const handleSubmit = async (values: ApplicationFormValues) => {
    setPaymentError(null);
    setAddressNotice(null);

    const confirmPayment = confirmPaymentRef.current;

    if (!confirmPayment) {
      setPaymentError('Please add a payment method before submitting.');
      return;
    }

    let setupIntentId: string;

    try {
      setupIntentId = await confirmPayment();
    } catch (error: any) {
      setPaymentError(
        error?.message || 'We could not save your payment method.'
      );
      return;
    }

    await finishApplication(setupIntentId, values);
  };

  return (
    <Formik<ApplicationFormValues>
      innerRef={formikRef}
      initialValues={initialValues}
      validate={validateApplication}
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      {(formik: FormikProps<ApplicationFormValues>) => {
        // Card path: Formik's flag covers confirm. Wallet / provision: hook flag.
        const showSubmitting = formik.isSubmitting || isSubmitting;

        return (
          <>
            <MembershipApplicationOverlay visible={showSubmitting} />
            <ApplicationBody
              formik={formik}
              steps={steps}
              onConfirmReady={handleConfirmReady}
              paymentStatus={paymentStatus}
              onPaymentStatusChange={setPaymentStatus}
              paymentChoice={paymentChoice}
              onPaymentChoiceChange={(choice) => {
                // A decline belongs to the method that was declined.
                setPaymentError(null);
                setErrorMessage(null);
                setCorrectWalletBilling(false);
                setPaymentChoice(choice);
              }}
              walletType={walletType}
              onWalletTypeChange={setWalletType}
              onWalletConfirmed={(setupIntentId) =>
                finishApplication(setupIntentId, formik.values)
              }
              submissionError={paymentError ?? errorMessage}
              addressNotice={addressNotice}
              correctWalletBilling={correctWalletBilling}
              isSubmitting={showSubmitting}
              user={user}
              subs={subs}
              insuranceData={insuranceData}
              previewCart={previewCart}
              previewCartUpdated={previewCartUpdated}
              referralCopy={referralCopy}
              onCommitValues={(committed) => {
                onCommitValues(committed);
                if (
                  ambassadorRedirectModalEnabled &&
                  isBrandAmbassadorCode(committed.referralCode)
                ) {
                  showAmbassadorRedirectModal({
                    ...formik.values,
                    ...committed
                  });
                }
              }}
              suppressAbandon={suppressAbandon}
            />
          </>
        );
      }}
    </Formik>
  );
}

export default SinglePageApplication;
